import React, { useCallback } from "react";
import { Form } from "antd";
import { useTranslate } from "@refinedev/core";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { useAddonDraft } from "../../context/PlaybookDraftContext";
import { PodcastFormValues } from "./types";
import { PODCAST_DEFAULTS } from "./defaults";
import { useAudioConfigData, type VoiceOption } from "./hooks/useAudioConfigData";
import { CONFIG_ITEM } from "../../data/addons";
import { PodcastSettingsFields } from "./podcast/PodcastSettingsFields";

// TODO: replace with a real credit estimate once the backend exposes one for
// this add-on. Hardcoded for now (MED-6822).
const ESTIMATED_CREDITS = 120;

export interface PodcastConfigCardViewProps {
  /** Selectable voices (an org avatar's voice profile). */
  voiceOptions: VoiceOption[];
  voicesLoading?: boolean;
  initialValues: PodcastFormValues;
  onValuesChange: (
    changedValues: Partial<PodcastFormValues>,
    values: PodcastFormValues
  ) => void;
}

/**
 * Pure settings card for the "Podcast" playbook add-on (Step 2 customize view).
 *
 * The backend requires exactly two voices in `voice_profile_ids`, positionally
 * assigned to the speakers, so the card renders one select per role
 * ([[PODCAST_SPEAKER_ROLES]]) bound to its own array index instead of a single
 * free-for-all multi-select.
 */
export const PodcastConfigCardView = ({
  voiceOptions,
  voicesLoading,
  initialValues,
  onValuesChange,
}: PodcastConfigCardViewProps) => {
  const t = useTranslate();
  const [form] = Form.useForm<PodcastFormValues>();

  // Each role writes to its own array index, so filling only the second role
  // would leave a hole (`[undefined, id]`) that serializes to `null` and is
  // rejected by the API. Compact it before it reaches the draft.
  const handleValuesChange = useCallback(
    (changedValues: Partial<PodcastFormValues>, values: PodcastFormValues) =>
      onValuesChange(changedValues, {
        ...values,
        generation_settings: {
          ...values.generation_settings,
          voice_profile_ids: (
            values.generation_settings.voice_profile_ids ?? []
          ).filter(Boolean),
        },
      }),
    [onValuesChange]
  );

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Podcast}
      title={t("createExperience.addOns.podcast.title")}
      credits={ESTIMATED_CREDITS}
      description={t("createExperience.addOns.podcast.description")}
    >
      <Form<PodcastFormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <PodcastSettingsFields
          voiceOptions={voiceOptions}
          voicesLoading={voicesLoading}
          initialVoiceProfileIds={initialValues.generation_settings.voice_profile_ids}
        />
      </Form>
    </PlaybookAddOnCard>
  );
};

/**
 * Wires the pure card to the org's voices and the shared PlaybookDraft context
 * (via `useAddonDraft`), so values survive switching between cards.
 */
export const PodcastConfigCard: React.FC = () => {
  // Emotion-capable voices only: the podcast script carries emotion tags and
  // generation fails outright on a voice that can't speak them.
  const { emotionCapableAvatarOptions, avatarsLoading } = useAudioConfigData();
  const { initialValues, onValuesChange } = useAddonDraft(
    CONFIG_ITEM.Podcast,
    PODCAST_DEFAULTS
  );

  return (
    <PodcastConfigCardView
      voiceOptions={emotionCapableAvatarOptions}
      voicesLoading={avatarsLoading}
      initialValues={initialValues}
      onValuesChange={onValuesChange}
    />
  );
};

export default PodcastConfigCard;
